import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/atoms/card';
import { Button } from '@/components/atoms/button';
import { Badge } from '@/components/atoms/badge';
import { Progress } from '@/components/atoms/progress';
import { Skeleton } from '@/components/atoms/skeleton';
import { Separator } from '@/components/atoms/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import { useSubscription } from '../hooks/use-subscription';
import { useAvailablePlans } from '../hooks/use-available-plans';
import { useCancelSubscription } from '../hooks/use-cancel-subscription';
import {
  Check,
  X,
  Crown,
  AlertTriangle,
  Calendar,
  Store,
  Package,
  Users,
  BarChart3,
  Megaphone,
  Gift,
  Trash2,
  Sparkles,
  Zap,
  Rocket,
  Percent,
} from 'lucide-react';
import { format, differenceInDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { formatPrice } from '@/lib/utils';

export function SubscriptionContainer() {
  const { t } = useTranslation();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  const { data: subscription, isLoading: isLoadingSubscription } = useSubscription();
  const { data: plans, isLoading: isLoadingPlans } = useAvailablePlans();
  const { mutate: cancelSubscription, isPending: isCancelling } = useCancelSubscription();

  const handleCancelSubscription = () => {
    cancelSubscription(undefined, {
      onSuccess: () => setCancelDialogOpen(false),
    });
  };

  if (isLoadingSubscription || isLoadingPlans) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-8 w-48" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </CardContent>
        </Card>
        <div className="grid gap-4 md:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <Skeleton className="h-40 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const getStatusBadge = (status?: string) => {
    switch (status) {
      case 'ACTIVE':
        return (
          <Badge className="bg-green-500/10 text-green-600 border-green-500/20">
            {t('settings.subscription.status.active')}
          </Badge>
        );
      case 'TRIALING':
        return (
          <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/20">
            {t('settings.subscription.status.trialing')}
          </Badge>
        );
      case 'PAST_DUE':
        return (
          <Badge className="bg-red-500/10 text-red-600 border-red-500/20">
            {t('settings.subscription.status.pastDue')}
          </Badge>
        );
      case 'CANCELLED':
      case 'EXPIRED':
        return (
          <Badge variant="outline">
            {t('settings.subscription.status.cancelled')}
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getPlanIcon = (planName: string) => {
    switch (planName.toLowerCase()) {
      case 'free':
        return <Sparkles className="size-6" />;
      case 'medium':
        return <Zap className="size-6" />;
      case 'ultim':
        return <Rocket className="size-6" />;
      default:
        return <Crown className="size-6" />;
    }
  };

  const getPlanGradient = (planName: string, isCurrentPlan: boolean) => {
    if (isCurrentPlan) {
      return 'bg-gradient-to-br from-primary/20 via-primary/10 to-transparent border-primary/50';
    }
    switch (planName.toLowerCase()) {
      case 'free':
        return 'bg-gradient-to-br from-gray-100 to-transparent dark:from-gray-800';
      case 'medium':
        return 'bg-gradient-to-br from-blue-50 to-transparent dark:from-blue-950';
      case 'ultim':
        return 'bg-gradient-to-br from-purple-50 to-transparent dark:from-purple-950';
      default:
        return '';
    }
  };

  const daysRemaining = subscription
    ? differenceInDays(new Date(subscription.endDate), new Date())
    : 0;

  return (
    <div className="space-y-8">
      {/* Current Subscription */}
      {subscription && (
        <Card className="overflow-hidden">
          <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="size-14 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                  <Crown className="size-7" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-bold">{subscription.planName}</h2>
                    {getStatusBadge(subscription.status)}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t('settings.subscription.paymentSource')}: {subscription.paymentSource}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">{t('settings.subscription.daysRemaining')}</p>
                <p className="text-3xl font-bold text-primary">{daysRemaining}</p>
              </div>
            </div>
          </div>

          <CardContent className="pt-6 space-y-6">
            {/* Period */}
            <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50">
              <Calendar className="size-5 text-muted-foreground" />
              <div className="flex-1">
                <p className="text-sm font-medium">{t('settings.subscription.period')}</p>
                <p className="text-xs text-muted-foreground">
                  {format(new Date(subscription.startDate), 'dd MMM yyyy', { locale: fr })} → {format(new Date(subscription.endDate), 'dd MMM yyyy', { locale: fr })}
                </p>
              </div>
              <Progress value={(1 - daysRemaining / 30) * 100} className="w-24 h-2" />
            </div>

            {subscription.cancelAtPeriodEnd && (
              <div className="flex items-center gap-3 p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                <AlertTriangle className="size-5 text-yellow-600" />
                <p className="text-sm text-yellow-600">
                  {t('settings.subscription.willCancelAt', {
                    date: format(new Date(subscription.endDate), 'dd MMMM yyyy', { locale: fr }),
                  })}
                </p>
              </div>
            )}

            <Separator />

            {/* Features */}
            <div>
              <h4 className="font-medium mb-4">{t('settings.subscription.yourLimits')}</h4>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                  <Store className="size-5 text-purple-500" />
                  <div className="flex-1">
                    <p className="text-sm">{t('settings.subscription.maxShops')}</p>
                    <p className="font-bold">{subscription.features.maxShops}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                  <Package className="size-5 text-blue-500" />
                  <div className="flex-1">
                    <p className="text-sm">{t('settings.subscription.maxProducts')}</p>
                    <p className="font-bold">{subscription.features.maxProducts}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                  <BarChart3 className="size-5 text-green-500" />
                  <div className="flex-1">
                    <p className="text-sm">{t('settings.subscription.metricsLevel')}</p>
                    <p className="font-bold">
                      {subscription.features.metricsLevel === 0
                        ? t('settings.subscription.basic')
                        : subscription.features.metricsLevel === 1
                        ? t('settings.subscription.advanced')
                        : t('settings.subscription.premium')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                  <Gift className="size-5 text-yellow-500" />
                  <div className="flex-1">
                    <p className="text-sm">{t('settings.subscription.freeAdsPerYear')}</p>
                    <p className="font-bold">{subscription.features.freeAdsPerYear}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                  <Percent className="size-5 text-orange-500" />
                  <div className="flex-1">
                    <p className="text-sm">{t('settings.subscription.commissionRate')}</p>
                    <p className="font-bold">{subscription.features.commissionRate}%</p>
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Feature Toggles */}
            <div>
              <h4 className="font-medium mb-4">{t('settings.subscription.includedFeatures')}</h4>
              <div className="grid gap-2 sm:grid-cols-2">
                <FeatureToggle
                  icon={Users}
                  label={t('settings.subscription.multipleEmployees')}
                  enabled={subscription.features.hasMultipleEmployeeAccounts}
                />
                <FeatureToggle
                  icon={Megaphone}
                  label={t('settings.subscription.adsAccess')}
                  enabled={subscription.features.adsAccess}
                />
              </div>
            </div>

            {/* Cancel Button */}
            {subscription.status === 'ACTIVE' && subscription.paymentSource !== 'FREE' && !subscription.cancelAtPeriodEnd && (
              <>
                <Separator />
                <Button
                  variant="outline"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setCancelDialogOpen(true)}
                >
                  <Trash2 className="size-4 mr-2" />
                  {t('settings.subscription.cancel')}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Available Plans */}
      {plans && plans.length > 0 && (
        <div>
          <div className="mb-6">
            <h3 className="text-xl font-bold">{t('settings.subscription.availablePlans')}</h3>
            <p className="text-muted-foreground">{t('settings.subscription.choosePlan')}</p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {plans.filter(p => p.isEnabled).map((plan) => {
              const isCurrentPlan = subscription?.planId === plan.id;
              const price = parseFloat(plan.prices.usd);

              return (
                <Card
                  key={plan.id}
                  className={`relative overflow-hidden transition-all hover:shadow-lg ${getPlanGradient(plan.name, isCurrentPlan)} ${isCurrentPlan ? 'ring-2 ring-primary' : ''}`}
                >
                  {isCurrentPlan && (
                    <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-xs px-3 py-1 rounded-bl-lg font-medium">
                      {t('settings.subscription.currentPlan')}
                    </div>
                  )}

                  <CardHeader className="pb-4">
                    <div className="flex items-center gap-3">
                      <div className={`size-12 rounded-xl flex items-center justify-center ${
                        plan.name.toLowerCase() === 'free'
                          ? 'bg-gray-100 text-gray-600 dark:bg-gray-800'
                          : plan.name.toLowerCase() === 'medium'
                          ? 'bg-blue-100 text-blue-600 dark:bg-blue-900'
                          : 'bg-purple-100 text-purple-600 dark:bg-purple-900'
                      }`}>
                        {getPlanIcon(plan.name)}
                      </div>
                      <div>
                        <CardTitle>{plan.name}</CardTitle>
                        <CardDescription>
                          {plan.maxProducts} {t('settings.subscription.products')} · {plan.maxShops} {t('settings.subscription.shops')}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-6">
                    {/* Price */}
                    <div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-bold">
                          {price === 0 ? t('settings.subscription.free') : formatPrice(price, 'USD')}
                        </span>
                        {price > 0 && (
                          <span className="text-muted-foreground">/{t('settings.subscription.month')}</span>
                        )}
                      </div>
                      {price > 0 && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {formatPrice(plan.prices.comoros, 'KMF')} · {formatPrice(plan.prices.euro, 'EUR')}
                        </p>
                      )}
                    </div>

                    <Separator />

                    {/* Features List */}
                    <ul className="space-y-3">
                      <PlanFeature
                        enabled={true}
                        label={`${plan.maxShops} ${t('settings.subscription.shops')}`}
                      />
                      <PlanFeature
                        enabled={true}
                        label={`${plan.maxProducts} ${t('settings.subscription.products')}`}
                      />
                      <PlanFeature
                        enabled={plan.hasMultipleEmployeeAccounts}
                        label={t('settings.subscription.multipleEmployees')}
                      />
                      <PlanFeature
                        enabled={plan.adsAccess}
                        label={t('settings.subscription.adsAccess')}
                      />
                      <PlanFeature
                        enabled={plan.priceComparaison}
                        label={t('settings.subscription.priceComparison')}
                      />
                      <PlanFeature
                        enabled={plan.canDeleteProducts}
                        label={t('settings.subscription.canDeleteProducts')}
                      />
                      <PlanFeature
                        enabled={plan.canDeleteShops}
                        label={t('settings.subscription.canDeleteShops')}
                      />
                      {plan.freeAdsPerYear > 0 && (
                        <PlanFeature
                          enabled={true}
                          label={`${plan.freeAdsPerYear} ${t('settings.subscription.freeAdsPerYear')}`}
                        />
                      )}
                      <li className="flex items-center gap-2 text-sm text-foreground">
                        <Percent className="size-4 text-orange-500 shrink-0" />
                        {t('settings.subscription.commissionRate')}: {plan.commissionRate}%
                      </li>
                    </ul>

                    {/* CTA Button */}
                    <Button
                      className="w-full"
                      variant={isCurrentPlan ? 'outline' : 'default'}
                      disabled={isCurrentPlan}
                    >
                      {isCurrentPlan
                        ? t('settings.subscription.currentPlan')
                        : price === 0
                        ? t('settings.subscription.downgrade')
                        : t('settings.subscription.upgrade')}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Cancel Subscription Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('settings.subscription.cancelTitle')}</DialogTitle>
            <DialogDescription>
              {t('settings.subscription.cancelDescription')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button variant="destructive" onClick={handleCancelSubscription} disabled={isCancelling}>
              {isCancelling ? t('settings.subscription.cancelling') : t('settings.subscription.confirmCancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FeatureToggle({
  icon: Icon,
  label,
  enabled,
}: {
  icon: React.ElementType;
  label: string;
  enabled: boolean;
}) {
  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg ${enabled ? 'bg-green-500/5 border border-green-500/20' : 'bg-muted/50'}`}>
      <Icon className={`size-4 ${enabled ? 'text-green-500' : 'text-muted-foreground'}`} />
      <span className={`text-sm flex-1 ${enabled ? 'text-foreground' : 'text-muted-foreground'}`}>{label}</span>
      {enabled ? (
        <Check className="size-4 text-green-500" />
      ) : (
        <X className="size-4 text-muted-foreground" />
      )}
    </div>
  );
}

function PlanFeature({ enabled, label }: { enabled: boolean; label: string }) {
  return (
    <li className={`flex items-center gap-2 text-sm ${enabled ? 'text-foreground' : 'text-muted-foreground line-through'}`}>
      {enabled ? (
        <Check className="size-4 text-green-500 shrink-0" />
      ) : (
        <X className="size-4 text-muted-foreground shrink-0" />
      )}
      {label}
    </li>
  );
}
